<template>
  <div class="verify-box">
    <div class="verify-main">
      <h3 v-if="process.needIdentityCheck || process.needIdentityAndPasswordCheck">{{$t('verifyBox.identityCheck')}}</h3>
      <h3 v-if="process.needPassword || process.passwordError">{{$t('verifyBox.passwordCheck')}}</h3>
      <span class="verify-desc" v-if="process.needIdentityCheck || process.needIdentityAndPasswordCheck">{{$t('verifyBox.verifyDesc')}}</span>
      <span class="verify-desc" v-if="process.needPassword || process.passwordError">{{$t('verifyBox.passwordDesc')}}</span>
      <div class="verify-content">
        <van-field
          v-model="phone"
          :placeholder="$t('verifyBox.inputPhone')"
          clearable
          style="margin-bottom: 24px;"
          v-if="process.needIdentityCheck || process.needIdentityAndPasswordCheck"
        >
          <div slot="button" class="code-button" @click="getCodeAction" >{{sendCodeButtonPrompt}}</div>
        </van-field>
        <van-field
          v-model="code"
          :placeholder="$t('verifyBox.inputCodePlaceholder')"
          clearable
          v-if="process.needIdentityCheck || process.needIdentityAndPasswordCheck"
        />

        <small class="password-desc" v-if="process.needIdentityAndPasswordCheck">{{$t('verifyBox.passwordDesc')}}</small>
        <van-field
          v-model="password"
          :placeholder="$t('verifyBox.inputPasswordPlaceholder')"
          clearable
          v-if="process.needPassword || process.passwordError || process.needIdentityAndPasswordCheck"
        />
        <small class="error-message" v-if="process.errorMsg ">{{process.errorMsg}}</small>
        <small class="error-message" v-if="process.passwordErrorMsg">{{process.passwordErrorMsg}}</small>
      </div>
      <div class="verify-button">
        <van-button type="primary" :disabled="!canSubmit" @click="handleSubmit">{{$t('verifyBox.submitButtonPrompt')}}</van-button>
      </div>
    </div>
  </div>
</template>

<script>
import {i18n} from '@/utils/i18n'
import ajax from '@/utils/ajax'
import { toast, initSlidingCaptcha } from '@/utils/common'

const MAX_COUNT = 60

export default {
  name: 'VerifyBox',
  i18n,
  props: {
    process: {
      type: Object,
      required: true
    },
    uniqueName: {
      type: String,
      default: ''
    },
    external: {
      type: Boolean,
      default: false
    },
    isMoreShare: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      phone: '',
      code: '',
      timerLeftCount: MAX_COUNT,
      timer: null,
      isSendingCode: false,
      sendCodePrompt: i18n.t('verifyBox.getCodeButtonPrompt'),
      captchaInstance: null,
      captchaVerified: false,
      password: ''
    }
  },
  computed: {
    sendCodeButtonPrompt () {
      if (this.timerLeftCount === MAX_COUNT) {
        return this.sendCodePrompt
      } else {
        return `${this.timerLeftCount}s`
      }
    },
    canSubmit () {
      if (this.process.needIdentityAndPasswordCheck) {
        return this.phone && this.code && this.password
      } else if (this.process.needIdentityCheck) {
        return this.phone && this.code
      } else {
        return this.password
      }
    }
  },
  watch: {
    phone: 'clearError',
    code: 'clearError',
    password: 'clearError'
  },
  beforeDestroy () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  },
  methods: {
    clearError () {
      if (this.process.errorMsg) {
        this.process.errorMsg = ''
      }
      if (this.process.passwordErrorMsg) {
        this.process.passwordErrorMsg = ''
      }
    },
    sendMsmCode (res) {
      ajax({
        url: '/apps/verification/send_code',
        data: {
          request_id: res.request_id,
          identity: this.phone,
          type: 'share_captcha',
          unique_name: this.uniqueName
        }
      }).then(result => {
        this.isSendingCode = false
        if (result.success) {
          toast(i18n.t('verifyBox.sendCodeSuccess'))
          // 清除之前的定时器（防止重复调用）
          if (this.timer) {
            clearInterval(this.timer)
          }
          // 重置倒计时为 MAX_COUNT，然后立即开始倒计时
          this.timerLeftCount = MAX_COUNT
          // 开始倒计时
          this.timer = setInterval(() => {
            if (this.timerLeftCount <= 1) {
              this.timerLeftCount = MAX_COUNT
              clearInterval(this.timer)
              this.timer = null
              this.captchaVerified = false
            } else {
              this.timerLeftCount--
            }
          }, 1000)
        } else {
          // 发送失败时重置状态
          this.timerLeftCount = MAX_COUNT
          toast(result.msg || i18n.t('verifyBox.sendCodeFail'))
        }
      }).catch(err => {
        console.error('Send msm code error:', err)
        this.isSendingCode = false
        this.timerLeftCount = MAX_COUNT
        toast('发送失败，请重试')
      })
    },
    validatePhoneOrEmail (value) {
      // 手机号验证（11位数字）
      const phoneRegex = /^1[3-9]\d{9}$/
      // 邮箱验证
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return phoneRegex.test(value) || emailRegex.test(value)
    },
    getCodeAction (event) {
      if (!this.phone) {
        toast(i18n.t('verifyBox.inputPhone'))
        return false
      }

      if (!this.validatePhoneOrEmail(this.phone)) {
        toast(i18n.t('verifyBox.inputPhoneOrEmail'))
        return false
      }

      // 检查倒计时状态和发送状态
      if (this.timerLeftCount !== MAX_COUNT) {
        return false
      }
      // 设置发送状态，防止重复点击
      this.isSendingCode = true
      initSlidingCaptcha(this.sendMsmCode)
    },
    handleSubmit () {
      if (!this.canSubmit) {
        return
      }
      // 提交验证信息
      this.$emit('verify-submit', {
        identity: this.phone,
        verification_code: this.code,
        password: this.password
      })
    }
  }
}
</script>

<style lang="postcss">
.verify-box {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  .verify-main {
    background-color: #fff;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 8px 80px 0 rgba(7, 22, 45, 0.08);
    h3 {
      color: #1D2531;
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      margin-bottom: 8px;
    }
    .verify-desc {
      color: #1D2531;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      margin-bottom: 40px;
      display: block;
    }
    .verify-content {
      margin-bottom: 40px;
      .code-button {
        color: #006BFF;
      }
      .van-field {
        border-radius: 8px;
        border: 1px solid #E5E5E5;
        background: var(---01-, #FFF);
      }
      .password-desc {
        color: var(---, #657083);
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        margin-bottom: 8px;
        margin-top: 40px;
        display: block;
      }
      .error-message {
        color: #FF5555;
        margin-top: 8px;
        display: block;
      }
    }
  }
  .verify-button {
    .van-button {
      width: 100%;
      height: 40px;
      line-height: 40px;
      border-radius: 8px;
      background: #006BFF;
      color: #fff;
    }
  }
}
</style>
